import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import * as cardValidator from "card-validator";
import moment from "moment";

export const INVALID_PASSWORD_MESSAGE =
  "Sua senha precisa ter pelo menos 8 caracteres, incluindo uma letra minúscula, uma maiúscula, um número e um caractere especial (!@#$%^&*).";

// MM/YYYY
export const expirationDateValidatorMMYYYY: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  if (!value) return null;
  return /^(0[1-9]|1[0-2])\/\d{4}$/.test(value) ? null : { invalidDate: { value } };
};

// MM/YY
export const expirationDateValidatorMMYY: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  if (!value) return null;

  // Check format MM/YY
  if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(value)) {
    return { invalidFormat: { value } };
  }

  // Check if date is in the future
  const [month, year] = value.split("/");
  const expirationDate = new Date(2000 + parseInt(year, 10), parseInt(month, 10) - 1);
  const currentDate = new Date();

  // Set current date to end of month for proper comparison
  currentDate.setDate(1);
  currentDate.setHours(0, 0, 0, 0);

  if (expirationDate < currentDate) {
    return { expired: { value } };
  }

  return null;
};

import { cpfCnpjValidator as createCpfCnpjValidator, cpfValidator as createCpfValidator } from "./brazilian-documents/cpf-cnpj.validator";

/** Validador Angular para CPF ou CNPJ (numérico/alfanumérico). */
export const cpfCnpjValidator = createCpfCnpjValidator();

/** Validador Angular exclusivo para CPF. */
export const cpfValidator = createCpfValidator();

export const futureDateValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  return moment(control.value).isSameOrAfter(moment().add(1, "days"), "day")
    ? null
    : { invalidDate: "Data deve ser amanhã ou futura" };
};

export const greaterThanZero: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = parseFloat(
    control.value
      ?.toString()
      .replace(/[^0-9,-]/g, "")
      .replace(",", "."),
  );
  return value > 0 ? null : { greaterThanZero: { value } };
};

export const cellphoneValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = (control.value || "").replace(/\D/g, "");
  return value.length === 10 || value.length === 11 ? null : { invalidCellphone: true };
};

export const passwordValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value || "";
  return /[A-Z]/.test(value) &&
    /[!"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~]/.test(value) &&
    /\d/.test(value) &&
    value.length >= 8
    ? null
    : { passwordInvalid: true };
};

export const passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const password = control.get("password");
  const confirmPassword = control.get("passwordConfirmation");
  if (!password || !confirmPassword) return null;

  // Se o campo de confirmação está vazio, não limpar o erro required
  if (!confirmPassword.value) {
    return null;
  }

  if (password.value !== confirmPassword.value) {
    confirmPassword.setErrors({ passwordsDontMatch: true });
    return { passwordsDontMatch: true };
  } else {
    confirmPassword.setErrors(null);
  }
  return null;
};

export const creditCardValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  return cardValidator.number(control.value).isValid ? null : { invalidCardNumber: { value: control.value } };
};

export const cardholderNameValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  if (!value) return null;

  // Permite apenas letras e espaços
  const validNameRegex = /^[A-Za-z\s]+$/;
  return validNameRegex.test(value) ? null : { invalidName: true };
};

// DD/MM/YYYY
export const birthDateValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value: string = control.value;
  if (!value) return null;

  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    return { invalidFormat: { value } };
  }

  const birthDate = moment(value, "DD/MM/YYYY", true);
  if (!birthDate.isValid() || birthDate.year() < 1900) {
    return { invalidDate: { value } };
  }

  if (birthDate.isAfter(moment(), "day")) {
    return { futureDate: { value } };
  }

  return null;
};
